import React, { useState, useEffect, useMemo } from 'react';
import './navigation.css';
import 'components/mobile-shell/MobilePageShellHeader.css';
import { getEnvConfig } from '../../config';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { UnauthenticatedTemplate, AuthenticatedTemplate } from '@azure/msal-react';
import { PermissionGate, PERMISSIONS } from 'utils/permissions';
import { Icons } from 'components/Icons';
import { useTranslation, LanguageDropdown } from 'utils/i18n';
import { useDarkMode } from 'utils/darkMode';
import { isPlatformPublicLightModeOnly } from 'utils/platformPublicLightModeOnly';
import { isMsalConfigValid } from 'utils/msalConfig';
import { useAuthState } from 'hooks/useAuthState';
import { useProfile } from 'utils/profileContext';
import Logo from 'components/Logo';
import ChatbotLogo from 'components/ChatbotLogo';
import BookmarksSection from 'components/BookmarksSection';
import { isBookmarked, getPageInfoForBookmarks, toggleBookmark } from 'utils/bookmarks';
import UserTypeDropdown from 'components/UserTypeDropdown';
import NotificationBell from 'components/NotificationBell';
import { useTenantContext } from 'contexts/TenantContext';
import { useProductContext } from 'contexts/ProductContext';
import logger from '../../utils/logger';
import { getPublicPageUrl } from 'utils/getPublicDomain';
import { useUserType, VIEW_MODES } from 'utils/userTypeContext';
import sharedTranslations from 'components/shared/translations';
import LanguageExpander from 'components/ui/LanguageExpander/LanguageExpander';
import { OutlineButton } from '../buttons';
import { 
  getOrSelectActiveAccount, 
  setActiveAccountPersistent, 
  getAllCachedAccounts,
  getAllAccountsWithHistory,
  getHistorySignInEmailByHomeAccountId,
  removeFromHistory,
  clearActiveAccountSelection,
  saveAccountToHistory,
  authMethods 
} from 'utils/msalConfig';
import { resolveExcludedAccountForSwitcherList } from 'utils/msalSwitcherExcludedAccount';
import {
  normalizeEmailForMatch,
  accountMatchesProfileEmail,
  getInitialsFromLabel,
  getAccountSwitcherRowLabels,
  filterAccountsForSwitcherList,
  filterHistoricalAccountsForSwitcher,
} from 'utils/msalAccountDisplay';
import { removeAccountFromMsalCache } from 'utils/msalRemoveAccountFromCache';
import { getApiClient } from 'utils/apiClient';
import { useMongoAccountDisplayByEmailMap } from 'hooks/useMongoAccountDisplayByEmailMap';

// Centralized logo configuration - this is the golden standard
export const STANDARD_LOGO_CONFIG = {
  showTagline: true,
  showVerticalLine: true
};

const Header = ({ sidebarOpen, setSidebarOpen, onSignIn, showSidebarToggle = true }) => {
  // CRITICAL: React hooks must be called unconditionally at the top level
  // Always call useMsal() - it will return null/undefined if MSAL provider is not available
  const msalData = useMsal();
  
  // CRITICAL: If onSignIn is provided (unauth-customer-portal), don't use MSAL at all
  // This prevents MSAL from being initialized on the public/unauthenticated site
  let instance = null;
  let accounts = [];
  
  // Only use MSAL if onSignIn is NOT provided (authenticated customer-portal)
  if (!onSignIn && msalData) {
    instance = msalData.instance;
    accounts = msalData.accounts || [];
  } else if (onSignIn) {
    // Unauth-customer-portal: MSAL should not be used
    logger.debug('HEADER', 'onSignIn provided - skipping MSAL (unauth-customer-portal redirect)');
    instance = null;
    accounts = [];
  }
  
  // Use the new authentication state hook
  const { user } = useAuthState();
  
  // Use profile context for updated display name
  const { getDisplayInfo, profile } = useProfile();
  
  // Use product context to check if Besper product is selected
  const { selectedProduct } = useProductContext();
  
  // Use user type context to check view mode
  const { viewMode } = useUserType();
  
  const location = useLocation();
  const navigate = useNavigate();
  const { t: tCommon, language } = useTranslation();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  
  // Get shared component translations
  const getSharedTranslation = (key, defaultValue = '') => {
    const translations = sharedTranslations[language] || sharedTranslations.en;
    const value = key.split('.').reduce((obj, k) => obj?.[k], translations);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    // Fallback to common translations
    const commonValue = tCommon(key);
    if (commonValue !== key && commonValue !== undefined) {
      return commonValue;
    }
    return defaultValue !== '' ? defaultValue : key;
  };
  
  const t = (key, defaultValue = '') => {
    // Try shared translations first
    const sharedValue = getSharedTranslation(key, '');
    if (sharedValue !== key && sharedValue !== '') {
      return sharedValue;
    }
    // Fallback to common translations
    return tCommon(key, defaultValue);
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [secondarySidebarOpen, setSecondarySidebarOpen] = useState(false);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); // 'language' | 'userType' | null
  const [isNotificationBellHovered, setIsNotificationBellHovered] = useState(false);
  const [allCachedAccounts, setAllCachedAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState(null);

  const excludedAccountForSwitcher = useMemo(
    () => resolveExcludedAccountForSwitcherList(instance, profile?.email),
    [instance, profile?.email],
  );

  const profileEmailNorm = normalizeEmailForMatch(profile?.email);

  const cachedAccountsForSwitcher = useMemo(
    () =>
      filterAccountsForSwitcherList(
        allCachedAccounts,
        activeAccount,
        excludedAccountForSwitcher,
        profileEmailNorm,
      ),
    [allCachedAccounts, activeAccount, excludedAccountForSwitcher, profileEmailNorm],
  );

  const userMenuRef = React.useRef(null);
  const userMenuButtonRef = React.useRef(null);
  const secondarySidebarRef = React.useRef(null);
  const swipeOpenStartRef = React.useRef(null);
  const [canImpersonate, setCanImpersonate] = useState(false);
  const impersonateCheckDoneRef = React.useRef(false);

  // Sidebar state tracking (no logging)

  // When user dropdown opens, check once if current user has impersonate permission (from compiled security context)
  useEffect(() => {
    if (!userMenuOpen || impersonateCheckDoneRef.current) return;
    impersonateCheckDoneRef.current = true;
    getApiClient()
      .get('/v1/cache/my-security-context')
      .then((res) => {
        if (!res?.data?.permissions || typeof res.data.permissions !== 'object') return;
        const perms = res.data.permissions;
        for (const tenantId of Object.keys(perms)) {
          const containers = perms[tenantId];
          if (containers && typeof containers === 'object' && containers.users) {
            const users = containers.users;
            if (users && (users.impersonate === true || users.admin === true || users.all === true)) {
              setCanImpersonate(true);
              return;
            }
          }
        }
      })
      .catch(() => {});
  }, [userMenuOpen]);

  // Close secondary sidebar when main sidebar closes
  useEffect(() => {
    if (!mobileMenuOpen) {
      setSecondarySidebarOpen(false);
      setSelectedGroup(null);
    }
  }, [mobileMenuOpen]);

  // Swipe-to-go-back functionality for secondary sidebar on mobile
  useEffect(() => {
    if (!isMobile || !secondarySidebarOpen || !secondarySidebarRef.current) {
      return;
    }

    const sidebar = secondarySidebarRef.current;
    let touchStartX = 0;
    let touchStartY = 0;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      if (touch) {
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
      }
    };

    const handleTouchMove = (e) => {
      const touch = e.touches[0];
      if (touch && touchStartX > 0) {
        const deltaX = touch.clientX - touchStartX;
        const deltaY = Math.abs(touch.clientY - touchStartY);

        // Only trigger if horizontal swipe is dominant (more horizontal than vertical)
        // and swipe is to the right (positive deltaX)
        if (Math.abs(deltaX) > deltaY && deltaX > 50) {
          // Swipe right detected - go back to main menu
          setSecondarySidebarOpen(false);
          setSelectedGroup(null);
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    sidebar.addEventListener('touchstart', handleTouchStart, { passive: true });
    sidebar.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      sidebar.removeEventListener('touchstart', handleTouchStart);
      sidebar.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isMobile, secondarySidebarOpen]);

  // Close user menu when clicking outside or clicking other header buttons.
  // Use a short delay so moving mouse to a link doesn't close and reopen (flicker).
  const closeUserMenuTimeoutRef = React.useRef(null);
  useEffect(() => {
    if (!userMenuOpen) return;

    const handleClickOutside = (event) => {
      const clickedInsideDropdown = userMenuRef.current && userMenuRef.current.contains(event.target);
      const clickedOnUserMenuButton = userMenuButtonRef.current && userMenuButtonRef.current.contains(event.target);

      if (!clickedInsideDropdown && !clickedOnUserMenuButton) {
        const target = event.target.closest('button');
        if (target && target !== userMenuButtonRef.current) {
          const isHeaderControl = target.closest('.header-right, .controls-section');
          if (isHeaderControl) {
            setUserMenuOpen(false);
            return;
          }
        }
        if (closeUserMenuTimeoutRef.current) clearTimeout(closeUserMenuTimeoutRef.current);
        closeUserMenuTimeoutRef.current = setTimeout(() => setUserMenuOpen(false), 120);
      } else if (closeUserMenuTimeoutRef.current) {
        clearTimeout(closeUserMenuTimeoutRef.current);
        closeUserMenuTimeoutRef.current = null;
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (closeUserMenuTimeoutRef.current) {
        clearTimeout(closeUserMenuTimeoutRef.current);
        closeUserMenuTimeoutRef.current = null;
      }
    };
  }, [userMenuOpen]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);


  // Load all cached accounts + historical accounts for account switcher integration
  const [historicalAccounts, setHistoricalAccounts] = useState([]);

  const historicalAccountsForSwitcher = useMemo(
    () => filterHistoricalAccountsForSwitcher(historicalAccounts, activeAccount, profileEmailNorm),
    [historicalAccounts, activeAccount, profileEmailNorm],
  );

  const { byEmail: mongoAccountDisplayByEmail, byOid: mongoAccountDisplayByOid } =
    useMongoAccountDisplayByEmailMap(allCachedAccounts, historicalAccounts, instance);

  const [signInEmailHistoryRev, setSignInEmailHistoryRev] = useState(0);
  const historyEmailByHomeId = useMemo(
    () => getHistorySignInEmailByHomeAccountId(),
    [userMenuOpen, allCachedAccounts, historicalAccounts, signInEmailHistoryRev],
  );

  useEffect(() => {
    if (!instance) return;

    const loadAccounts = () => {
      const { cached, historical } = getAllAccountsWithHistory(instance);
      const active = getOrSelectActiveAccount(instance);
      
      setAllCachedAccounts(cached);
      setHistoricalAccounts(historical);
      setActiveAccount(active);
    };

    loadAccounts();
    
    // Refresh when accounts change (e.g., after login/logout)
    const interval = setInterval(loadAccounts, 2000);
    return () => clearInterval(interval);
  }, [instance]);

  useEffect(() => {
    if (!userMenuOpen || !instance) return;
    try {
      const all = instance.getAllAccounts() || [];
      for (const acc of all) {
        saveAccountToHistory(acc);
      }
      setSignInEmailHistoryRev((r) => r + 1);
    } catch (_) {}
  }, [userMenuOpen, instance]);

  useEffect(() => {
    if (!instance) return;
    let current = null;
    try {
      current = instance.getActiveAccount();
    } catch (_) {}
    if (current) return;

    const all = instance.getAllAccounts() || [];
    if (all.length < 2) return;

    const emailRaw = typeof profile?.email === 'string' ? profile.email.trim() : '';
    if (!emailRaw || !emailRaw.includes('@')) return;

    const normalized = normalizeEmailForMatch(emailRaw);
    const matches = all.filter((a) => accountMatchesProfileEmail(a, normalized));
    if (matches.length !== 1) return;

    setActiveAccountPersistent(instance, matches[0]);
    setActiveAccount(matches[0]);
    logger.info('HEADER', 'MSAL active account aligned with profile (single unambiguous email match)', {
      homeAccountId: matches[0].homeAccountId,
    });
  }, [instance, profile?.email, accounts.length]);

  // Enhanced mobile menu interaction handling
  useEffect(() => {
    const handleKeyDown = (event) => {
      // ESC key support for desktop users only
      if (event.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
        setSidebarOpen(false);
      }
    };

    const handleClickOutside = (event) => {
      // Close sidebar if clicking outside of it
      if (mobileMenuOpen) {
        const sidebar = document.querySelector('.nav-sidebar');
        const burgerButton = document.querySelector('.burger-menu-toggle');
        
        // Check if click is outside both sidebar and burger button
        if (sidebar && burgerButton) {
          const clickedInsideSidebar = sidebar.contains(event.target);
          const clickedOnBurgerButton = burgerButton.contains(event.target);
          
          if (!clickedInsideSidebar && !clickedOnBurgerButton) {
            setMobileMenuOpen(false);
            setSidebarOpen(false);
          }
        }
      }
    };

    const handleTouchStart = (event) => {
      // Store initial touch position for swipe detection
      const touch = event.touches[0];
      if (touch) {
        window.touchStartX = touch.clientX;
      }
    };

    const handleTouchEnd = (event) => {
      // Handle swipe-to-close: swipe right from left edge (slide sidebar right to close)
      const touch = event.changedTouches[0];
      if (touch && window.touchStartX != null && mobileMenuOpen) {
        const touchEndX = touch.clientX;
        const swipeDistance = touchEndX - window.touchStartX;
        if (swipeDistance > 80 && window.touchStartX < 50) {
          setMobileMenuOpen(false);
          setSidebarOpen(false);
        }
      }
      window.touchStartX = null;
    };

    if (mobileMenuOpen) {
      // Desktop keyboard support
      document.addEventListener('keydown', handleKeyDown);
      
      // Click outside to close
      document.addEventListener('click', handleClickOutside);
      
      // Mobile touch/swipe support
      document.addEventListener('touchstart', handleTouchStart, { passive: true });
      document.addEventListener('touchend', handleTouchEnd, { passive: true });
      
      // Note: We do NOT lock body scroll or change body positioning
      // to prevent layout shifts that affect sticky elements like product-tab-bar
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      window.touchStartX = null;
    };
  }, [mobileMenuOpen, setSidebarOpen]);

  // Mobile: swipe right from left edge to open sidebar
  useEffect(() => {
    if (!showSidebarToggle || !isMobile || mobileMenuOpen) return;

    const edgeZone = 24;
    const minSwipeRight = 50;

    const handleOpenTouchStart = (e) => {
      const touch = e.touches[0];
      if (touch && touch.clientX < edgeZone) {
        swipeOpenStartRef.current = { x: touch.clientX, y: touch.clientY };
      } else {
        swipeOpenStartRef.current = null;
      }
    };

    const handleOpenTouchEnd = (e) => {
      const touch = e.changedTouches[0];
      const start = swipeOpenStartRef.current;
      if (!touch || !start) {
        swipeOpenStartRef.current = null;
        return;
      }
      const deltaX = touch.clientX - start.x;
      const deltaY = Math.abs(touch.clientY - start.y);
      if (deltaX > minSwipeRight && deltaX > deltaY) {
        setMobileMenuOpen(true);
        setSidebarOpen(true);
      }
      swipeOpenStartRef.current = null;
    };

    document.addEventListener('touchstart', handleOpenTouchStart, { passive: true });
    document.addEventListener('touchend', handleOpenTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', handleOpenTouchStart);
      document.removeEventListener('touchend', handleOpenTouchEnd);
      swipeOpenStartRef.current = null;
    };
  }, [showSidebarToggle, isMobile, mobileMenuOpen, setSidebarOpen]);

  // Get language-aware path helper
  const getLocalizedPath = (path) => {
    const currentPath = location.pathname;
    const currentLangMatch = currentPath.match(/^\/([a-z]{2})\//);
    
    if (currentLangMatch) {
      return `/${language}${path}`;
    }
    return path;
  };

  const runAuthFlow = async (authCallback, actionLabel = t('header.signIn')) => {
    logger.debug('HEADER', 'runAuthFlow called', { actionLabel, hasCallback: !!authCallback, hasInstance: !!instance });
    const labelText = actionLabel || 'authenticate';
    const actionLabelLower = labelText.toLowerCase();
    try {
      if (!instance) {
        logger.error('HEADER', 'MSAL instance is not available - authentication is disabled');
        if (process.env.NODE_ENV === 'development') {
          alert(`Authentication is not configured for development mode, so we cannot ${actionLabelLower} right now.`);
        } else {
          alert(`Authentication is not properly configured. Unable to ${actionLabelLower}. Please contact support.`);
        }
        return;
      }

      const isValid = await isMsalConfigValid();
      if (!isValid) {
        logger.error('HEADER', 'MSAL configuration is invalid - cannot authenticate');
        if (process.env.NODE_ENV === 'development') {
          alert(`Authentication is not configured for development mode. This is normal for local development, but ${actionLabelLower} is unavailable.`);
        } else {
          alert(`Authentication is not properly configured. Unable to ${actionLabelLower}. Please contact support.`);
        }
        return;
      }

      const currentConfig = instance.getConfiguration();
      if (!currentConfig.auth.clientId || !currentConfig.auth.authority) {
        logger.error('HEADER', 'MSAL configuration is invalid - missing client or authority');
        alert(`Authentication is not properly configured. Unable to ${actionLabelLower}. Please contact support.`);
        return;
      }

      // BEST PRACTICE: Check accounts from useMsal hook instead of getActiveAccount()
      // The hook provides the authoritative account list (already available in component scope)
      // Use centralized active account helper
      const activeAccount = instance ? getOrSelectActiveAccount(instance) : null;
      if (activeAccount) {
        logger.info('HEADER', 'User is already authenticated', { username: activeAccount.username });
        return;
      }

      // Check interaction status using localStorage (MSAL v3 compatible)
      try {
        const interactionStatus = localStorage.getItem('msal.interaction.status');
        if (interactionStatus && interactionStatus !== 'null' && interactionStatus !== 'None') {
          logger.debug('HEADER', 'Clearing pending interaction', { interactionStatus });
          localStorage.removeItem('msal.interaction.status');
          authMethods.clearStuckAuth(instance);
        }
      } catch (clearError) {
        logger.warn('HEADER', 'Could not clear pending interactions', { error: clearError.message });
      }

      // CRITICAL: If interaction status is stuck (e.g. from abandoned redirect), clear it when user explicitly clicks Login
      try {
        if (typeof instance.getInteractionStatus === 'function') {
          const finalStatus = instance.getInteractionStatus();
          if (finalStatus !== 'None') {
            logger.warn('HEADER', `Interaction status was ${finalStatus}, clearing stuck state and proceeding with ${actionLabelLower}`);
            authMethods.clearStuckAuth(instance);
            // Brief delay so MSAL can clear in-memory state before we start redirect
            await new Promise((r) => setTimeout(r, 100));
          }
        } else {
          logger.debug('HEADER', 'getInteractionStatus not available, using inProgress from useMsal hook');
        }
      } catch (statusError) {
        logger.debug('HEADER', 'Could not verify interaction status', { error: statusError.message });
      }
      
      logger.info('HEADER', `Initiating ${actionLabelLower} with Entra External ID`);
      logger.debug('HEADER', 'About to call authCallback with instance', {
        hasInstance: !!instance,
        clientId: instance?.getConfiguration()?.auth?.clientId,
        authority: instance?.getConfiguration()?.auth?.authority
      });

      const result = authCallback?.(instance);
      if (result && typeof result.then === 'function') {
        try {
          await result;
        } catch (asyncError) {
          logger.error('HEADER', `Async ${actionLabelLower} error`, { error: asyncError.message, errorCode: asyncError.errorCode });
          if (asyncError.errorCode === 'interaction_in_progress') {
            authMethods.clearStuckAuth(instance);
            await new Promise((r) => setTimeout(r, 300));
            try {
              await authCallback?.(instance);
            } catch (retryError) {
              logger.error('HEADER', `Retry ${actionLabelLower} failed`, { error: retryError.message });
              alert(`Authentication is already in progress. Please wait for it to complete.`);
            }
          } else {
            alert(`${labelText} failed. Please try again or contact support.`);
          }
        }
      }
    } catch (error) {
      logger.error('HEADER', `${labelText} failed`, { error: error.message, errorCode: error.errorCode });

      if (error.errorCode === 'interaction_in_progress') {
        logger.info('HEADER', `${labelText} already in progress, clearing cache and retrying`);
        try {
          authMethods.clearStuckAuth(instance);
          await new Promise((r) => setTimeout(r, 300));
          await authCallback?.(instance);
        } catch (retryError) {
          logger.error('HEADER', 'Retry failed', { error: retryError.message });
          alert(`Authentication is already in progress. Please wait for the current ${actionLabelLower} attempt to complete.`);
        }
      } else {
        alert(`${labelText} failed. Please try again or contact support.`);
      }
    }
  };

  const handleAuth = () => {
    // CRITICAL: If onSignIn prop is provided (e.g., from unauth-customer-portal), use it instead
    // This prevents MSAL from being used on the public/unauthenticated site
    if (onSignIn && typeof onSignIn === 'function') {
      logger.debug('HEADER', 'Using custom onSignIn handler (unauth-customer-portal redirect)');
      onSignIn();
      return;
    }
    // Otherwise, use the standard MSAL auth flow (only for authenticated customer-portal)
    logger.debug('HEADER', 'Using standard MSAL auth flow');
    runAuthFlow(authMethods.login, t('header.signIn'));
  };
  // Note: Sign-up is handled automatically by the user flow when users click sign-in


  const handleLogout = () => {
    // Always use hard reset route for logout - ensures clean state even if MSAL is stuck
    // Clear any local UI state first
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    setSidebarOpen(false);
    
    // Navigate to hard reset route
    navigate('/auth/reset?reason=user_logout');
  };

  // Account switcher handlers - integrated into profile dropdown
  const handleSwitchAccount = async (account) => {
    if (!instance || !account) return;

    try {
      setActiveAccountPersistent(instance, account);
      setActiveAccount(account);
      setUserMenuOpen(false);
      
      // Reload page to refresh all contexts with new account
      window.location.reload();
    } catch (error) {
      logger.error('HEADER', 'Failed to switch account', { error: error.message });
    }
  };

  const handleAddAccount = async () => {
    if (!instance) return;

    try {
      setUserMenuOpen(false);
      await authMethods.login(instance, { prompt: 'login' });
    } catch (error) {
      logger.error('HEADER', 'Failed to add account', { error: error.message });
    }
  };

  const handleSignInWithHistoricalAccount = async (historicalAccount) => {
    if (!instance || !historicalAccount) return;

    try {
      setUserMenuOpen(false);
      // Sign in with specific account using loginHint
      await authMethods.login(instance, { 
        prompt: 'select_account',
        loginHint: historicalAccount.username || historicalAccount.name
      });
    } catch (error) {
      logger.error('HEADER', 'Failed to sign in with historical account', { error: error.message });
    }
  };

  const handleRemoveFromHistory = async (historicalAccount) => {
    if (!historicalAccount || !historicalAccount.homeAccountId) return;

    try {
      removeFromHistory(historicalAccount.homeAccountId);
      // Reload accounts to update UI
      const { cached, historical } = getAllAccountsWithHistory(instance);
      setAllCachedAccounts(cached);
      setHistoricalAccounts(historical);
    } catch (error) {
      logger.error('HEADER', 'Failed to remove from history', { error: error.message });
    }
  };

  const handleRemoveAccount = async (account) => {
    if (!instance || !account) return;

    try {
      await removeAccountFromMsalCache(instance, account);
      
      // If this was the active account, clear selection
      if (activeAccount && activeAccount.homeAccountId === account.homeAccountId) {
        clearActiveAccountSelection(instance);
        setActiveAccount(null);
      }
      
      // Reload accounts
      const cachedAccounts = getAllCachedAccounts(instance);
      setAllCachedAccounts(cachedAccounts);
      
      // If no accounts left, reload page
      if (cachedAccounts.length === 0) {
        window.location.reload();
      } else {
        // Select first remaining account
        const newActive = getOrSelectActiveAccount(instance);
        if (newActive) {
          setActiveAccountPersistent(instance, newActive);
          setActiveAccount(newActive);
        }
      }
    } catch (error) {
      logger.error('HEADER', 'Failed to remove account', { error: error.message });
    }
  };

  const getUserInfo = () => {
    // Use profile context as primary source (includes updated display name)
    try {
      const profileDisplayInfo = getDisplayInfo();
      if (profileDisplayInfo && profileDisplayInfo.name && 
          profileDisplayInfo.name !== 'User' && 
          profileDisplayInfo.name.toLowerCase() !== 'unknown') {
        return profileDisplayInfo;
      }
    } catch (error) {
      // Profile context not available, fall back to auth state
    }
    
    // Use the user state from the authentication hook as fallback
    if (user && user.name && user.name.toLowerCase() !== 'unknown') {
      return user;
    }
    
    // Use activeAccount from state only (set by useEffect). NEVER call getOrSelectActiveAccount or
    // setActiveAccountPersistent during render - they trigger MSAL synchronously and cause
    // useMsal setState → re-render → stack overflow (RangeError: Maximum call stack size exceeded).
    let account = activeAccount;
    
    // BEST PRACTICE: Never fallback to accounts[0] when multiple accounts exist
    // This prevents wrong-account refresh cycles that cause AADSTS160021 loops
    // If no active account is selected and multiple accounts exist, user must choose explicitly
    if (!account) {
      if (accounts && accounts.length > 1) {
        logger.warn('HEADER', 'Multiple accounts exist but no active account selected. User must choose via account switcher');
        return { name: 'Select Account', initials: '?', email: '' };
      }
      // Single account case: use for display only. Do NOT call setActiveAccountPersistent during
      // render (effect with loadAccounts() sets active account; calling it here causes recursion).
      if (accounts && accounts.length === 1) {
        account = accounts[0];
      } else {
        // No accounts or multiple accounts without selection
        return { name: 'User', initials: 'U', email: '' };
      }
    }
    
    if (account) {
      // For Entra External ID, check idTokenClaims for name/email information
      const idTokenClaims = account.idTokenClaims || {};

      // Helper to check if a value is a valid user-readable name (not a GUID, not "unknown")
      const isValidDisplayName = (value) => {
        if (!value || typeof value !== 'string') return false;
        const trimmed = value.trim();
        if (!trimmed) return false;
        // Reject if it's "unknown", a GUID, or an email address (we'll use email separately)
        if (trimmed.toLowerCase() === 'unknown') return false;
        if (/^[0-9a-fA-F-]{32,}$/.test(trimmed)) return false; // GUID
        return true;
      };

      const isLikelyUserEmail = (value) => {
        if (!value || typeof value !== 'string') return false;
        const looksLike = value.includes('@') && value.includes('.') && !/onmicrosoft\.com$/i.test(value);
        const notGuid = !/^[0-9a-fA-F-]{32,}$/.test(value);
        return looksLike && notGuid;
      };

      // Extract email - avoid using IDs or onmicrosoft aliases
      let email = null;
      const emailsArray = Array.isArray(idTokenClaims.emails) ? idTokenClaims.emails : null;
      if (isLikelyUserEmail(idTokenClaims.email)) email = idTokenClaims.email;
      else if (emailsArray && emailsArray.length && isLikelyUserEmail(emailsArray[0])) email = emailsArray[0];
      else if (isLikelyUserEmail(idTokenClaims.preferred_username)) email = idTokenClaims.preferred_username;
      else if (isLikelyUserEmail(account.username)) email = account.username;
      else if (isLikelyUserEmail(account.email)) email = account.email;
      
      // Only use 'sub' claim if it looks like an email (contains @)
      if (!email && idTokenClaims.sub && idTokenClaims.sub.includes('@')) {
        email = idTokenClaims.sub;
      }

      // Extract display name - prioritize actual name fields, fall back to email-derived name
      let displayName = null;
      
      // Try name from various sources (in priority order)
      if (isValidDisplayName(idTokenClaims.name)) displayName = idTokenClaims.name;
      else if (isValidDisplayName(account.name)) displayName = account.name;
      else if (isValidDisplayName(idTokenClaims.given_name)) {
        displayName = idTokenClaims.given_name;
        if (isValidDisplayName(idTokenClaims.family_name)) {
          displayName += ' ' + idTokenClaims.family_name;
        }
      }
      else if (isValidDisplayName(account.given_name)) displayName = account.given_name;
      
      // If no proper name found, derive from email
      if (!displayName && email) {
        // Extract name part from email (before @)
        const emailPart = email.split('@')[0];
        if (emailPart) {
          // Convert "firstname.lastname" or "firstname_lastname" to "Firstname Lastname"
          displayName = emailPart
            .replace(/[._]/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        }
      }
      
      // Final fallback
      if (!displayName) {
        displayName = 'User';
      }
      
      // Create initials from display name
      const initials = displayName
        .split(' ')
        .filter(n => n.length > 0)
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2) || 'U';
      
      return {
        name: displayName,
        initials: initials,
        email: email || ''
      };
    }
    return { name: 'User', initials: 'U', email: '' };
  };

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  // Check if a navigation item is bookmarked
  const isNavItemBookmarked = (path) => {
    const pageInfo = getPageInfoForBookmarks(path, '');
    return isBookmarked(pageInfo.path);
  };

  // Handle bookmark toggle for navigation items
  const handleBookmarkToggle = (e, itemPath) => {
    e.preventDefault();
    e.stopPropagation();
    
    const pageInfo = getPageInfoForBookmarks(itemPath, '');
    const success = toggleBookmark(pageInfo);
    
    if (success) {
      // Dispatch custom event to update bookmarks section immediately
      window.dispatchEvent(new CustomEvent('bookmarkChanged', {
        detail: { action: 'toggle', path: itemPath }
      }));
    }
  };

  const toggleMobileMenu = (e) => {
    // Prevent event from bubbling to click-outside handler
    if (e) {
      e.stopPropagation();
    }
    const newState = !mobileMenuOpen;
    setMobileMenuOpen(newState);
    setSidebarOpen(newState);
    if (!newState) {
      // Close secondary sidebar when main sidebar closes
      setSecondarySidebarOpen(false);
      setSelectedGroup(null);
    }
  };


  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };


  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setSidebarOpen(false);
    setSecondarySidebarOpen(false);
    setSelectedGroup(null);
  };

  const { name, initials, email } = getUserInfo();
  const { isMainTenantPartner } = useTenantContext();

  // Get environment - hide Press Kit if not dev (fe-shared getEnvConfig)
  const envConfig = getEnvConfig();
  const environment = (envConfig.ENVIRONMENT || (process.env.NODE_ENV === 'development' ? 'dev' : 'prod')).toLowerCase();
  const isDev = environment === 'dev';

  // Navigation groups data structure
  const navigationGroups = {
    unauthenticated: [
      {
        id: 'company-product',
        title: t('header.navigationGroups.companyProduct.title'),
        items: [
          { path: '/about', label: t('header.about') },
          { path: '/architecture', label: t('header.navigationGroups.companyProduct.items.architecture', 'Architecture') },
          { path: '/events', label: t('header.navigationGroups.companyProduct.items.events', 'Events'), upcoming: true },
          { path: '/partners', label: t('header.navigationGroups.companyProduct.items.partners'), upcoming: true },
          { path: '/marketplace', label: t('header.navigationGroups.companyProduct.items.marketplace') },
          { path: '/testimonials', label: t('header.navigationGroups.companyProduct.items.testimonials'), upcoming: true },
          { path: '/become-partner', label: t('header.navigationGroups.companyProduct.items.becomePartner') },
          { path: '/press', label: t('header.navigationGroups.companyProduct.items.press'), upcoming: true },
          { path: '/investors', label: t('header.navigationGroups.companyProduct.items.investors'), upcoming: true }
        ]
      },
      {
        id: 'resources-support',
        title: t('header.navigationGroups.resourcesSupport.title'),
        items: [
          { path: '/docs/getting-started/quickstart', label: t('footer.support.developerDocs', 'Developer docs') },
          { path: '/help-center', label: t('header.navigationGroups.resourcesSupport.items.helpCenter') },
          { path: '/faq', label: t('header.navigationGroups.resourcesSupport.items.faq') },
          { path: '/sla', label: t('header.navigationGroups.resourcesSupport.items.sla'), upcoming: true },
          { path: '/community', label: t('header.navigationGroups.resourcesSupport.items.community') },
          { path: '/feedback', label: t('header.navigationGroups.resourcesSupport.items.feedback') },
          { path: '/contact', label: t('header.contact') }
        ]
      },
      {
        id: 'news-updates',
        title: t('header.navigationGroups.newsUpdates.title'),
        items: [
          { path: '/changelog', label: t('header.navigationGroups.newsUpdates.items.changelog'), upcoming: true },
          { path: '/newsletter', label: t('header.navigationGroups.newsUpdates.items.newsletter'), upcoming: true }
        ]
      },
      {
        id: 'legal-privacy',
        title: t('header.navigationGroups.legalPrivacy.title'),
        items: [
          { path: '/terms', label: t('header.navigationGroups.legalPrivacy.items.terms') },
          { path: '/privacy', label: t('header.navigationGroups.legalPrivacy.items.privacy') },
          { path: '/eula', label: t('header.navigationGroups.legalPrivacy.items.eula') },
          { path: '/acceptable-use', label: t('header.navigationGroups.legalPrivacy.items.acceptableUse') },
          { path: '/disclaimer', label: t('header.navigationGroups.legalPrivacy.items.disclaimer') },
          { path: '/privacy-policy', label: t('header.navigationGroups.legalPrivacy.items.privacyPolicy') },
          { path: '/gdpr', label: t('header.navigationGroups.legalPrivacy.items.gdpr') },
          { path: '/dpa', label: t('header.navigationGroups.legalPrivacy.items.dpa') },
          { path: '/cookie-settings', label: t('header.navigationGroups.legalPrivacy.items.cookieSettings') },
          { path: '/impressum', label: t('header.navigationGroups.legalPrivacy.items.impressum') }
        ]
      },
      ...(isDev ? [{
        id: 'press-kit',
        title: t('header.navigationGroups.pressKit.title'),
        items: [
          { path: '/styling-mock', label: t('header.navigationGroups.pressKit.items.overview') },
          { path: '/styling-mock/buttons', label: t('header.navigationGroups.pressKit.items.buttons') },
          { path: '/styling-mock/containers', label: t('header.navigationGroups.pressKit.items.containers') },
          { path: '/styling-mock/ui', label: t('header.navigationGroups.pressKit.items.ui') },
          { path: '/styling-mock/typography', label: t('header.navigationGroups.pressKit.items.typography') },
          { path: '/styling-mock/logo', label: t('header.navigationGroups.pressKit.items.logo') },
          { path: '/styling-mock/layouts', label: t('header.navigationGroups.pressKit.items.layouts') },
          { path: '/styling-mock/animations', label: t('header.navigationGroups.pressKit.items.animations') },
          { path: '/styling-mock/navigation', label: 'Navigation' }
        ]
      }] : [])
    ],
    authenticated: [
      {
        id: 'user-access',
        title: t('header.navigationGroups.userAccess.title'),
        items: [
          { path: '/?tab=users', label: t('header.users') },
          { path: '/?tab=roles', label: t('header.navigationGroups.userAccess.items.roles') }
        ]
      },
      {
        id: 'support-resources',
        title: t('header.navigationGroups.supportResources.title'),
        items: [
          { path: '/roadmap', label: t('header.navigationGroups.supportResources.items.roadmap') },
          { path: '/faq', label: t('header.navigationGroups.resourcesSupport.items.faq') },
          { path: '/docs/getting-started/quickstart', label: t('footer.support.developerDocs', 'Developer docs') },
          { path: '/bug-reports', label: t('header.navigationGroups.supportResources.items.bugReports') },
          { path: '/help-center', label: t('header.navigationGroups.supportResources.items.helpCenter') },
          { path: '/community', label: t('header.navigationGroups.resourcesSupport.items.community') },
          { path: '/become-partner', label: t('header.navigationGroups.companyProduct.items.becomePartner') },
          { path: '/support', label: t('header.navigationGroups.supportResources.items.support'), upcoming: true },
          { path: '/inquiries', label: t('header.navigationGroups.supportResources.items.inquiries'), upcoming: true },
          { path: '/contact', label: t('header.contact') },
          { path: '/feedback', label: t('header.navigationGroups.supportResources.items.feedback') }
        ]
      },
      ...(isDev ? [{
        id: 'press-kit',
        title: t('header.navigationGroups.pressKit.title'),
        items: [
          { path: '/styling-mock', label: t('header.navigationGroups.pressKit.items.overview') },
          { path: '/styling-mock/buttons', label: t('header.navigationGroups.pressKit.items.buttons') },
          { path: '/styling-mock/containers', label: t('header.navigationGroups.pressKit.items.containers') },
          { path: '/styling-mock/ui', label: t('header.navigationGroups.pressKit.items.ui') },
          { path: '/styling-mock/typography', label: t('header.navigationGroups.pressKit.items.typography') },
          { path: '/styling-mock/logo', label: t('header.navigationGroups.pressKit.items.logo') },
          { path: '/styling-mock/layouts', label: t('header.navigationGroups.pressKit.items.layouts') },
          { path: '/styling-mock/animations', label: t('header.navigationGroups.pressKit.items.animations') },
          { path: '/styling-mock/navigation', label: 'Navigation' }
        ]
      }] : []),
      // Conditionally include confidential resources only for partner main tenant
      ...(isMainTenantPartner ? [{
        id: 'confidential-resources',
        title: t('header.navigationGroups.confidentialResources.title'),
        items: [
          { path: '/partner-pricing', label: t('header.navigationGroups.confidentialResources.items.partnerPricing'), target: '_blank', mobileDisabled: true },
          { path: '/partner-pitch-deck', label: t('header.navigationGroups.confidentialResources.items.partnerPitchDeck'), target: '_blank', mobileDisabled: true }
        ]
      }] : [])
    ]
  };

  // Helper function to convert kebab-case to camelCase for translation keys
  const kebabToCamel = (str) => {
    return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
  };

  // Group click handler
  const handleGroupClick = (group) => {
    logger.debug('HEADER', 'Group clicked', { groupId: group.id, groupTitle: group.title });
    setSelectedGroup(group);
    setSecondarySidebarOpen(true);
  };

  // Close secondary sidebar
  const closeSecondarySidebar = () => {
    setSecondarySidebarOpen(false);
    setSelectedGroup(null);
  };

  return (
    <header 
      className={`header ${isMobile ? 'header-mobile' : ''} ${isNotificationBellHovered ? 'notification-bell-hovered' : ''}`}
      onMouseEnter={() => setIsNotificationBellHovered(true)}
      onMouseLeave={() => setIsNotificationBellHovered(false)}
    >
      <div
        className={
          isMobile
            ? 'mobile-page-shell-header mobile-page-shell-header--global-app-inner'
            : 'header-container'
        }
      >
        <div className="header-content">
          {/* Left side: Burger menu and logo */}
          <div className="header-left">
            {showSidebarToggle && (
              <button 
                className="burger-menu-toggle"
                onClick={toggleMobileMenu}
                title={t('header.mobileMenu.openNavigationMenu')}
                aria-label={t('header.mobileMenu.openNavigationMenu')}
                aria-expanded={mobileMenuOpen}
              >
                <span className="hamburger-icon">☰</span>
              </button>
            )}

            <div className="logo-section">
              <Link to={getLocalizedPath('/')} onClick={closeMobileMenu}>
                {selectedProduct === 'besperBot' ? (
                  <div className="header-chatbot-logo">
                    <ChatbotLogo inverted={true} size={40} />
                  </div>
                ) : (
                  <Logo {...STANDARD_LOGO_CONFIG} />
                )}
              </Link>
            </div>
          </div>
          
          {/* Right side: Controls */}
          <div className="header-right">
            <div className="controls-section desktop-only">
              {instance && (
                <AuthenticatedTemplate>
                  {/* Notification Bell - moved to left of controls */}
                  <NotificationBell />
                </AuthenticatedTemplate>
              )}
              
              <LanguageDropdown />
              
              {/* View Public Homepage Button - Forward to platform-public (local port / cloud URL, same rules as footer unauth links) */}
              {instance && (location.pathname === '/' || location.pathname.match(/^\/[a-z]{2}\/?$/)) && (
                <AuthenticatedTemplate>
                  <a
                    className="view-public-homepage-toggle"
                    href={getPublicPageUrl(location.pathname || '/')}
                    onClick={() => setUserMenuOpen(false)}
                    title={t('header.viewPublicHomepage', 'View public homepage')}
                    aria-label={t('header.viewPublicHomepage', 'View public homepage')}
                  >
                    <Icons.Eye size={16} />
                  </a>
                </AuthenticatedTemplate>
              )}
              
              {!isPlatformPublicLightModeOnly() && (
              <button 
                className="dark-mode-toggle" 
                onClick={() => {
                  toggleDarkMode();
                  setUserMenuOpen(false);
                }}
                title={isDarkMode ? t('header.lightMode') : t('header.darkMode')}
                aria-label={isDarkMode ? t('header.lightMode') : t('header.darkMode')}
              >
                {isDarkMode ? <Icons.Sun size={16} /> : <Icons.Moon size={16} />}
              </button>
              )}
              
              {/* Show sign-in button: 
                  - If onSignIn is provided (unauth-customer-portal), show directly without MSAL templates
                  - If MSAL instance exists (customer-portal), use UnauthenticatedTemplate
              */}
              {onSignIn ? (
                // Unauth-customer-portal: Show sign-in button directly (no MSAL templates)
                <>
                  {/* Only show user type dropdown on homepage */}
                  {location.pathname === '/' || location.pathname.match(/^\/[a-z]{2}\/?$/) ? (
                    <UserTypeDropdown />
                  ) : null}
                  
                  <div className="header-auth-buttons">
                    <OutlineButton onClick={handleAuth} size="medium" className="header-auth-signin">
                      {t('header.signIn')}
                    </OutlineButton>
                  </div>
                </>
              ) : instance ? (
                // Customer-portal: Use MSAL templates
                <UnauthenticatedTemplate>
                  {/* Only show user type dropdown on homepage */}
                  {location.pathname === '/' || location.pathname.match(/^\/[a-z]{2}\/?$/) ? (
                    <UserTypeDropdown />
                  ) : null}
                  
                  <div className="header-auth-buttons">
                    <OutlineButton onClick={handleAuth} size="medium" className="header-auth-signin">
                      {t('header.signIn')}
                    </OutlineButton>
                  </div>
                </UnauthenticatedTemplate>
              ) : null}
              
              {instance && (
                <AuthenticatedTemplate>
                  {/* View Mode switcher for authenticated users */}
                  <UserTypeDropdown />
                  
                  <div className="user-info desktop-only">
                    {/* Unified Profile Menu with Account Switcher integrated */}
                    <div className="user-menu-container">
                      <button
                        ref={userMenuButtonRef}
                        className="user-avatar-btn"
                        onClick={toggleUserMenu}
                        title={t('header.userMenu.title')}
                        aria-label={t('header.userMenu.openUserMenu')}
                        aria-expanded={userMenuOpen}
                      >
                        <span className="user-avatar">{initials}</span>
                        <span className={`dropdown-arrow ${userMenuOpen ? 'open' : ''}`}>
                          <Icons.ChevronDown size={10} />
                        </span>
                      </button>
                      
                      {userMenuOpen && (
                        <div ref={userMenuRef} className="user-dropdown" style={{ minWidth: '320px' }}>
                          {/* Current Account Section */}
                          <div className="user-dropdown-header">
                            <div className="user-dropdown-avatar">
                              <span className="user-dropdown-avatar-text">{initials}</span>
                            </div>
                            <div className="user-dropdown-info">
                              <div className="user-dropdown-name">{name}</div>
                              {email && (
                                <div className="user-dropdown-email" title={email}>
                                  {email}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="user-dropdown-divider"></div>
                          
                          {/* Profile & Settings Links */}
                          <Link 
                            to={getLocalizedPath('/profile')} 
                            className="dropdown-item" 
                            onClick={() => setUserMenuOpen(false)}
                            title={t('header.userMenu.navigateToProfile')}
                          >
                            <Icons.Profile size={20} />
                            <span style={{ fontSize: '15px', fontWeight: '500' }}>{t('header.profile')}</span>
                          </Link>
                          
                          <Link 
                            to={getLocalizedPath('/app-users')} 
                            className="dropdown-item" 
                            onClick={() => setUserMenuOpen(false)}
                            title={t('header.userMenu.manageApplicationUsers')}
                          >
                            <Icons.Users size={20} />
                            <span style={{ fontSize: '15px', fontWeight: '500' }}>{t('header.userMenu.applicationUsers')}</span>
                          </Link>
                          
                          <Link 
                            to={getLocalizedPath('/profile?tab=notifications')} 
                            className="dropdown-item" 
                            onClick={() => setUserMenuOpen(false)}
                            title={t('header.userMenu.notificationPreferencesTitle')}
                          >
                            <Icons.Bell size={20} />
                            <span style={{ fontSize: '15px', fontWeight: '500' }}>{t('header.userMenu.notificationPreferences')}</span>
                          </Link>
                          
                          {/* Payments link - Only shown for NORMAL view mode (B2C customers) */}
                          {viewMode === VIEW_MODES.NORMAL && (
                            <Link 
                              to={getLocalizedPath('/payment-profile')} 
                              className="dropdown-item" 
                              onClick={() => setUserMenuOpen(false)}
                              title={t('header.userMenu.payments', 'Manage payment profile and billing')}
                            >
                              <Icons.Money size={20} />
                              <span style={{ fontSize: '15px', fontWeight: '500' }}>{t('header.userMenu.payments', 'Payments')}</span>
                            </Link>
                          )}
                          
                          {/* Account Switcher Section - Professional unified account list */}
                          {(cachedAccountsForSwitcher.length > 0 ||
                            historicalAccountsForSwitcher.length > 0) && (
                            <>
                              <div className="user-dropdown-divider"></div>
                              <div style={{ 
                                padding: '14px 16px 10px', 
                                fontSize: '11px', 
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                color: 'var(--color-text-secondary)',
                                borderBottom: '1px solid var(--color-border)',
                                textAlign: 'left'
                              }}>
                                {t('header.userMenu.yourAccounts', 'Your Accounts')}
                              </div>
                              
                              {/* Active/Logged-in accounts (other than current) */}
                              {cachedAccountsForSwitcher.map((account) => {
                                  const { primary: accLabel, emailLine } = getAccountSwitcherRowLabels(
                                    account,
                                    mongoAccountDisplayByEmail,
                                    historyEmailByHomeId,
                                    mongoAccountDisplayByOid,
                                  );
                                  const accInitials = getInitialsFromLabel(accLabel);
                                  return (
                                    <div
                                      key={account.homeAccountId}
                                      className="account-switcher-item"
                                      style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '12px', 
                                        width: '100%',
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        transition: 'background 0.15s ease',
                                        borderBottom: '1px solid var(--color-border)'
                                      }}
                                      onClick={() => handleSwitchAccount(account)}
                                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                      {/* Avatar with status indicator */}
                                      <div style={{ position: 'relative', flexShrink: 0 }}>
                                        <span className="user-avatar" style={{ 
                                          width: '40px', 
                                          height: '40px', 
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          background: 'linear-gradient(135deg, var(--color-primary), #0066cc)',
                                          color: 'white',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '50%'
                                        }}>
                                          {accInitials || 'U'}
                                        </span>
                                        {/* Green status dot - logged in */}
                                        <span style={{
                                          position: 'absolute',
                                          bottom: '0',
                                          right: '0',
                                          width: '12px',
                                          height: '12px',
                                          background: '#22c55e',
                                          borderRadius: '50%',
                                          border: '2px solid var(--color-bg-primary)',
                                          boxShadow: '0 0 0 1px rgba(34, 197, 94, 0.3)'
                                        }} title={t('header.userMenu.signedIn', 'Signed in')} />
                                      </div>
                                      
                                      {/* Account info */}
                                      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                                        <div
                                          style={{
                                            fontWeight: '500',
                                            fontSize: '14px',
                                            color: 'var(--color-text-primary)',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'left',
                                          }}
                                          title={emailLine ? `${accLabel}\n${emailLine}` : accLabel}
                                        >
                                          {accLabel}
                                        </div>
                                        {emailLine ? (
                                          <div
                                            style={{
                                              fontSize: '12px',
                                              color: 'var(--color-text-secondary)',
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              marginTop: '2px',
                                              textAlign: 'left',
                                            }}
                                            title={emailLine}
                                          >
                                            {emailLine}
                                          </div>
                                        ) : null}
                                      </div>
                                      
                                      {/* Actions */}
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSwitchAccount(account);
                                          }}
                                          style={{
                                            padding: '6px 14px',
                                            background: 'var(--color-primary)',
                                            border: 'none',
                                            borderRadius: '6px',
                                            color: 'white',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            transition: 'all 0.15s ease'
                                          }}
                                          title="Switch to this account"
                                        >
                                          Switch
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveAccount(account);
                                          }}
                                          style={{
                                            padding: '6px 8px',
                                            background: 'transparent',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: '6px',
                                            color: 'var(--color-text-secondary)',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            lineHeight: 1,
                                            transition: 'all 0.15s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#ef4444';
                                            e.currentTarget.style.color = '#ef4444';
                                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.05)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--color-border)';
                                            e.currentTarget.style.color = 'var(--color-text-secondary)';
                                            e.currentTarget.style.background = 'transparent';
                                          }}
                                          title={t('header.userMenu.removeAccount', 'Remove account')}
                                        >
                                          <Icons.Close size={14} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              
                              {/* Historical/Signed-out accounts */}
                              {historicalAccountsForSwitcher.map((historicalAccount) => {
                                const { primary: histLabel, emailLine: histEmailLine } =
                                  getAccountSwitcherRowLabels(
                                    historicalAccount,
                                    mongoAccountDisplayByEmail,
                                    historyEmailByHomeId,
                                    mongoAccountDisplayByOid,
                                  );
                                const histInitials = getInitialsFromLabel(histLabel);
                                return (
                                  <div
                                    key={historicalAccount.homeAccountId}
                                    className="account-switcher-item"
                                    style={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: '12px', 
                                      width: '100%',
                                      padding: '12px 16px',
                                      cursor: 'pointer',
                                      transition: 'background 0.15s ease',
                                      borderBottom: '1px solid var(--color-border)',
                                      opacity: 0.85
                                    }}
                                    onClick={() => handleSignInWithHistoricalAccount(historicalAccount)}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = 'var(--color-bg-secondary)';
                                      e.currentTarget.style.opacity = '1';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'transparent';
                                      e.currentTarget.style.opacity = '0.85';
                                    }}
                                  >
                                    {/* Avatar with status indicator */}
                                    <div style={{ position: 'relative', flexShrink: 0 }}>
                                      <span className="user-avatar" style={{ 
                                        width: '40px', 
                                        height: '40px', 
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        background: 'var(--color-bg-tertiary, #e5e7eb)',
                                        color: 'var(--color-text-secondary)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%',
                                        border: '2px dashed var(--color-border)'
                                      }}>
                                        {histInitials || 'U'}
                                      </span>
                                      {/* Gray status dot - signed out */}
                                      <span style={{
                                        position: 'absolute',
                                        bottom: '0',
                                        right: '0',
                                        width: '12px',
                                        height: '12px',
                                        background: '#9ca3af',
                                        borderRadius: '50%',
                                        border: '2px solid var(--color-bg-primary)'
                                      }} title={t('header.userMenu.signedOut', 'Signed out')} />
                                    </div>
                                    
                                    {/* Account info */}
                                    <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                                      <div
                                        style={{
                                          fontWeight: '500',
                                          fontSize: '14px',
                                          color: 'var(--color-text-primary)',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          textAlign: 'left',
                                        }}
                                        title={
                                          histEmailLine ? `${histLabel}\n${histEmailLine}` : histLabel
                                        }
                                      >
                                        {histLabel}
                                      </div>
                                      {histEmailLine ? (
                                        <div
                                          style={{
                                            fontSize: '12px',
                                            color: 'var(--color-text-secondary)',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            marginTop: '2px',
                                            textAlign: 'left',
                                          }}
                                          title={histEmailLine}
                                        >
                                          {histEmailLine}
                                        </div>
                                      ) : null}
                                    </div>
                                    
                                    {/* Actions */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSignInWithHistoricalAccount(historicalAccount);
                                        }}
                                        style={{
                                          padding: '6px 14px',
                                          background: 'transparent',
                                          border: '1px solid var(--color-primary)',
                                          borderRadius: '6px',
                                          color: 'var(--color-primary)',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          fontWeight: '500',
                                          transition: 'all 0.15s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.background = 'var(--color-primary)';
                                          e.currentTarget.style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background = 'transparent';
                                          e.currentTarget.style.color = 'var(--color-primary)';
                                        }}
                                        title="Sign in to this account"
                                      >
                                        Sign in
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveFromHistory(historicalAccount);
                                        }}
                                        style={{
                                          padding: '6px 8px',
                                          background: 'transparent',
                                          border: '1px solid var(--color-border)',
                                          borderRadius: '6px',
                                          color: 'var(--color-text-secondary)',
                                          cursor: 'pointer',
                                          fontSize: '14px',
                                          lineHeight: 1,
                                          transition: 'all 0.15s ease',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.borderColor = '#ef4444';
                                          e.currentTarget.style.color = '#ef4444';
                                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.05)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.borderColor = 'var(--color-border)';
                                          e.currentTarget.style.color = 'var(--color-text-secondary)';
                                          e.currentTarget.style.background = 'transparent';
                                        }}
                                        title={t('header.userMenu.removeFromHistory', 'Remove from history')}
                                      >
                                        <Icons.Close size={14} />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          )}
                          
                          <div className="user-dropdown-divider"></div>
                          
                          {/* Add Account Button */}
                          <button
                            className="dropdown-item"
                            onClick={handleAddAccount}
                            style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                          >
                            <span style={{
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '18px',
                              fontWeight: '500'
                            }}>+</span>
                            <span style={{ fontSize: '15px', fontWeight: '500' }}>{t('header.userMenu.addAccount', 'Add another account')}</span>
                          </button>

                          {canImpersonate && (
                            <Link
                              to={getLocalizedPath('/profile') + '#impersonation'}
                              className="dropdown-item"
                              onClick={() => setUserMenuOpen(false)}
                              title={t('header.userMenu.impersonateUserTitle', 'Impersonate user — your requests are sent as the selected user; the backend verifies each request.')}
                              style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                            >
                              <Icons.UserAdd size={20} />
                              <span style={{ fontSize: '15px', fontWeight: '500' }}>{t('header.userMenu.impersonateUser', 'Impersonate user')}</span>
                            </Link>
                          )}

                          <div className="user-dropdown-divider"></div>

                          <button className="dropdown-item logout-btn" onClick={handleLogout}>
                            <Icons.Logout size={20} />
                            <span style={{ fontSize: '15px', fontWeight: '500' }}>{t('header.signOut')}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </AuthenticatedTemplate>
              )}
            </div>
            
          </div>
        </div>
      </div>
      
      {/* Main Navigation Sidebar - Groups Only */}
      {showSidebarToggle && mobileMenuOpen && (
        <nav className={`nav-sidebar ${mobileMenuOpen ? 'nav-sidebar-open' : ''}`}>
          <div className="nav-sidebar-content">
            {/* Navigation header with close button */}
            <div className="nav-sidebar-header">
              <div className="nav-sidebar-title">
                <span className="menu-icon">☰</span>
                {t('header.navigation')}
              </div>
              <button 
                className="nav-sidebar-close-btn"
                onClick={closeMobileMenu}
                aria-label={t('header.mobileMenu.closeNavigationMenu')}
                title={t('header.mobileMenu.closeMenu')}
              >
                <span className="close-icon" aria-hidden="true">×</span>
              </button>
            </div>
            
            <ul className="nav-sidebar-groups">
              {/* Bookmarks Section */}
              <BookmarksSection onCloseMenu={closeMobileMenu} />
              
              {/* Always visible - Home */}
              <li>
                <Link 
                  to={getLocalizedPath('/')} 
                  className={`nav-group-item ${isActiveRoute('/') ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  {t('header.home')}
                </Link>
              </li>
              
              {/* Communications - Only for authenticated users */}
              {instance && (
                <AuthenticatedTemplate>
                  <li>
                    <Link 
                      to={getLocalizedPath('/communications')} 
                      className={`nav-group-item ${isActiveRoute('/communications') ? 'active' : ''}`}
                      onClick={closeMobileMenu}
                    >
                      <span className="nav-group-item-text">{t('header.communications')}</span>
                      {/* Unread count badge will be added here */}
                    </Link>
                  </li>
                </AuthenticatedTemplate>
              )}
              
              {/* Show unauthenticated navigation groups:
                  - If onSignIn is provided (platform-public/unauth site), show directly
                  - If MSAL instance exists (customer-portal), use UnauthenticatedTemplate
              */}
              {onSignIn ? (
                // Platform-public (unauth site): Show navigation groups directly
                navigationGroups.unauthenticated.map((group) => (
                  <li key={group.id}>
                    <button 
                      className="nav-group-item"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        logger.debug('HEADER', 'Button clicked for group', { groupTitle: group.title });
                        handleGroupClick(group);
                      }}
                      type="button"
                    >
                      {group.title}
                      <span className="nav-group-arrow">▶</span>
                    </button>
                  </li>
                ))
              ) : instance ? (
                // Customer-portal: Use MSAL templates
                <UnauthenticatedTemplate>
                  {navigationGroups.unauthenticated.map((group) => (
                  <li key={group.id}>
                    <button 
                      className="nav-group-item"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        logger.debug('HEADER', 'Button clicked for group', { groupTitle: group.title });
                        handleGroupClick(group);
                      }}
                      type="button"
                    >
                      {group.title}
                      <span className="nav-group-arrow">▶</span>
                    </button>
                  </li>
                  ))}
                </UnauthenticatedTemplate>
              ) : null}
              
              {instance && (
                <AuthenticatedTemplate>
                {navigationGroups.authenticated.map((group) => (
                  <li key={group.id}>
                    <button 
                      className="nav-group-item"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        logger.debug('HEADER', 'Button clicked for group', { groupTitle: group.title });
                        handleGroupClick(group);
                      }}
                      type="button"
                    >
                      {group.title}
                      <span className="nav-group-arrow">▶</span>
                    </button>
                  </li>
                ))}
                
                {/* Partner-only features */}
                <PermissionGate requiredPermissions={PERMISSIONS.PARTNER_FEATURES}>
                  <li>
                    <button 
                      className="nav-group-item"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        logger.debug('HEADER', 'Button clicked for group', { groupTitle: 'Partner Features' });
                        handleGroupClick({
                          id: 'partner-features',
                          title: t('header.navigationGroups.partnerFeatures.title'),
                          items: [
                            { path: '/partner-portal', label: t('header.navigationGroups.partnerFeatures.items.partnerPortal') }
                          ]
                        });
                      }}
                      type="button"
                    >
                      {t('header.navigationGroups.partnerFeatures.title')}
                      <span className="nav-group-arrow">▶</span>
                    </button>
                  </li>
                </PermissionGate>
                
                {/* Admin-only features */}
                <PermissionGate requiredPermissions={PERMISSIONS.FULL_ACCESS}>
                  <li>
                    <button 
                      className="nav-group-item"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        logger.debug('HEADER', 'Button clicked for group', { groupTitle: 'Administration' });
                        handleGroupClick({
                          id: 'administration',
                          title: t('header.navigationGroups.administration.title'),
                          items: [
                            { path: '/admin', label: t('header.navigationGroups.administration.items.adminPanel') }
                          ]
                        });
                      }}
                      type="button"
                    >
                      {t('header.navigationGroups.administration.title')}
                      <span className="nav-group-arrow">▶</span>
                    </button>
                  </li>
                </PermissionGate>
                </AuthenticatedTemplate>
              )}
            </ul>
            
            {/* Mobile Controls Section */}
            <div className="nav-sidebar-controls">
              {!isPlatformPublicLightModeOnly() && (
              <button 
                className="nav-sidebar-control-item" 
                onClick={() => {
                  toggleDarkMode();
                }}
              >
                <span className="nav-sidebar-control-icon">
                  {isDarkMode ? <Icons.Sun size={18} /> : <Icons.Moon size={18} />}
                </span>
                <span className="nav-sidebar-control-label">
                  {isDarkMode ? t('header.lightMode') : t('header.darkMode')}
                </span>
              </button>
              )}

              {/* Language Selection - Expandable on Mobile */}
              <div className="nav-sidebar-control-item nav-sidebar-language-item">
                <LanguageExpander />
              </div>

              {/* User Type (Partner/Customer) - Only on homepage for unauthenticated users */}
              {instance && (location.pathname === '/' || location.pathname.match(/^\/[a-z]{2}\/?$/)) && (
                <UnauthenticatedTemplate>
                  <div className="nav-sidebar-control-item nav-sidebar-user-type-item">
                    <div className="nav-sidebar-user-type-dropdown">
                      <UserTypeDropdown 
                        isOpen={activeDropdown === 'userType'}
                        onToggle={(open) => setActiveDropdown(open ? 'userType' : null)}
                      />
                    </div>
                  </div>
                </UnauthenticatedTemplate>
              )}

              {/* View Mode (Technical/Normal) - Always visible for authenticated users */}
              {instance && (
                <AuthenticatedTemplate>
                  <div className="nav-sidebar-control-item nav-sidebar-user-type-item">
                    <div className="nav-sidebar-user-type-dropdown">
                      <UserTypeDropdown 
                        isOpen={activeDropdown === 'userType'}
                        onToggle={(open) => setActiveDropdown(open ? 'userType' : null)}
                      />
                    </div>
                  </div>
                </AuthenticatedTemplate>
              )}
            </div>
            
            {/* Authentication Section at Bottom */}
            <div className="nav-sidebar-auth">
              {/* Show sign-in button in mobile menu: 
                  - If onSignIn is provided (unauth-customer-portal), show directly
                  - If MSAL instance exists (customer-portal), use UnauthenticatedTemplate
              */}
              {onSignIn ? (
                // Unauth-customer-portal: Show sign-in button directly (no MSAL templates)
                <div className="nav-sidebar-auth-buttons">
                  <OutlineButton 
                    onClick={() => {
                      handleAuth();
                      closeMobileMenu();
                    }}
                    size="medium"
                    className="nav-auth-signin"
                  >
                    {t('header.signIn')}
                  </OutlineButton>
                </div>
              ) : instance ? (
                // Customer-portal: Use MSAL templates
                <UnauthenticatedTemplate>
                  <div className="nav-sidebar-auth-buttons">
                    <OutlineButton 
                      onClick={() => {
                        handleAuth();
                        closeMobileMenu();
                      }}
                      size="medium"
                      className="nav-auth-signin"
                    >
                      {t('header.signIn')}
                    </OutlineButton>
                  </div>
                </UnauthenticatedTemplate>
              ) : null}
              
              {instance && (
                <AuthenticatedTemplate>
                  <div className="nav-auth-user">
                    <div 
                      className="nav-auth-user-info"
                      onClick={() => {
                        navigate(getLocalizedPath('/profile'));
                        closeMobileMenu();
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="nav-auth-user-avatar">
                        <span className="nav-auth-user-avatar-text">{initials}</span>
                      </div>
                      <div className="nav-auth-user-details">
                        <div className="nav-auth-user-name">{name}</div>
                        <div className="nav-auth-user-status">{t('header.mobileMenu.online')}</div>
                      </div>
                    </div>
                    <button 
                      className="nav-auth-btn nav-auth-signout"
                      onClick={() => {
                        handleLogout();
                        closeMobileMenu();
                      }}
                    >
                      <Icons.Logout size={16} />
                      <span>{t('header.signOut')}</span>
                    </button>
                  </div>
                </AuthenticatedTemplate>
              )}
            </div>
          </div>
        </nav>
      )}

      {/* Secondary Sidebar - Group Items */}
      {secondarySidebarOpen && selectedGroup && (
        <nav 
          ref={secondarySidebarRef}
          className={`nav-secondary-sidebar ${secondarySidebarOpen ? 'open' : ''}`}
        >
          <div className="nav-secondary-content">
            {/* Secondary sidebar header with breadcrumbs */}
            <div className="nav-secondary-header">
              <div className="nav-secondary-title">
                <button
                  className="nav-secondary-back-btn"
                  onClick={closeSecondarySidebar}
                  aria-label={t('header.mobileMenu.backToMainMenu', 'Back to main menu')}
                  title={t('header.mobileMenu.backToMainMenu', 'Back to main menu')}
                >
                  <Icons.ArrowLeft size={20} />
                </button>
                <div className="nav-secondary-breadcrumb">
                  <span className="breadcrumb-item">{t('header.mobileMenu.navigation', 'Navigation')}</span>
                  <span className="breadcrumb-separator">›</span>
                  <span className="breadcrumb-item breadcrumb-current">
                    {selectedGroup.id ? t(`header.navigationGroups.${kebabToCamel(selectedGroup.id)}.title`, selectedGroup.title) : selectedGroup.title}
                  </span>
                </div>
              </div>
              <button 
                className="nav-secondary-close-btn"
                onClick={closeSecondarySidebar}
                aria-label={t('header.mobileMenu.closeSecondaryMenu')}
                title={t('header.mobileMenu.closeMenu')}
              >
                <span className="close-icon" aria-hidden="true">×</span>
              </button>
            </div>
            
            <ul className="nav-secondary-links">
              {selectedGroup.items.map((item, index) => {
                const isBookmarked = isNavItemBookmarked(item.path);
                const isHovered = hoveredNavItem === item.path;
                
                return (
                  <li key={index}>
                    {item.upcoming ? (
                      <div 
                        className="nav-secondary-link upcoming-link" 
                        style={{
                          opacity: 0.6,
                          cursor: 'not-allowed',
                          pointerEvents: 'none',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 16px'
                        }}
                      >
                        <span className="nav-secondary-label" style={{color: '#666'}}>{item.label}</span>
                        {!isMobile && (
                          <span className="upcoming-badge" style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            backgroundColor: '#999',
                            color: '#fff',
                            borderRadius: '4px',
                            fontWeight: '500',
                            textTransform: 'uppercase',
                            marginLeft: '8px'
                          }}>Upcoming</span>
                        )}
                      </div>
                    ) : item.mobileDisabled && isMobile ? (
                      <div className="nav-secondary-link mobile-disabled" style={{
                        opacity: 0.6,
                        cursor: 'not-allowed',
                        pointerEvents: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        padding: '12px 16px'
                      }}>
                        <span className="nav-secondary-label" style={{color: '#666'}}>{item.label}</span>
                        <span className="mobile-disabled-text" style={{
                          fontSize: '12px',
                          color: '#999',
                          fontStyle: 'italic',
                          marginTop: '4px'
                        }}>{t('header.mobileMenu.notAvailableOnMobile')}</span>
                      </div>
                    ) : item.target === '_blank' ? (
                      <a 
                        href={getLocalizedPath(item.path)} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`nav-secondary-link ${isActiveRoute(item.path) ? 'active' : ''}`}
                        onClick={() => {
                          closeMobileMenu();
                          closeSecondarySidebar();
                        }}
                        onMouseEnter={() => setHoveredNavItem(item.path)}
                        onMouseLeave={() => setHoveredNavItem(null)}
                      >
                        <span className="nav-secondary-label">{item.label}</span>
                        <button
                          className="nav-secondary-bookmark-btn"
                          onClick={(e) => handleBookmarkToggle(e, item.path)}
                          title={isBookmarked ? t('header.mobileMenu.removeFromBookmarks') : t('header.mobileMenu.addToBookmarks')}
                          aria-label={isBookmarked ? t('header.mobileMenu.removeFromBookmarks') : t('header.mobileMenu.addToBookmarks')}
                        >
                          {isBookmarked ? (
                            <Icons.BookmarkFilled size={14} className="nav-secondary-bookmark bookmarked" />
                          ) : (
                            <Icons.Bookmark size={14} className={`nav-secondary-bookmark ${isHovered ? 'visible' : ''}`} />
                          )}
                        </button>
                      </a>
                    ) : (
                      <Link 
                        to={getLocalizedPath(item.path)} 
                        className={`nav-secondary-link ${isActiveRoute(item.path) ? 'active' : ''}`}
                        onClick={() => {
                          closeMobileMenu();
                          closeSecondarySidebar();
                        }}
                        onMouseEnter={() => setHoveredNavItem(item.path)}
                        onMouseLeave={() => setHoveredNavItem(null)}
                      >
                        <span className="nav-secondary-label">{item.label}</span>
                        <button
                          className="nav-secondary-bookmark-btn"
                          onClick={(e) => handleBookmarkToggle(e, item.path)}
                          title={isBookmarked ? t('header.mobileMenu.removeFromBookmarks') : t('header.mobileMenu.addToBookmarks')}
                          aria-label={isBookmarked ? t('header.mobileMenu.removeFromBookmarks') : t('header.mobileMenu.addToBookmarks')}
                        >
                          {isBookmarked ? (
                            <Icons.BookmarkFilled size={14} className="nav-secondary-bookmark bookmarked" />
                          ) : (
                            <Icons.Bookmark size={14} className={`nav-secondary-bookmark ${isHovered ? 'visible' : ''}`} />
                          )}
                        </button>
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;